import { useCallback, useEffect, useRef, useState } from "react";
import { Paperclip, Upload, Trash2, Loader2, FileText, Download, Eye } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WhatsAppButton } from "./WhatsApp";

const kb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const isImage = (f) => (f.content_type || "").startsWith("image/");
const isPdf = (f) => f.content_type === "application/pdf" || /\.pdf$/i.test(f.original_filename);
export const canPreview = (f) => isImage(f) || isPdf(f);

export const fetchBlob = async (f) => {
  const r = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
  return URL.createObjectURL(isPdf(f) ? new Blob([r.data], { type: "application/pdf" }) : r.data);
};

export function download(url, name) {
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
}

export function PreviewDialog({ file, onClose }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!file) return undefined;
    let u;
    fetchBlob(file).then((x) => { u = x; setUrl(x); }).catch(() => toast.error("Could not load preview"));
    return () => { if (u) URL.revokeObjectURL(u); setUrl(null); };
  }, [file]);

  return (
    <Dialog open={!!file} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="file-preview-dialog" className="max-w-5xl w-[95vw]">
        <DialogHeader>
          <DialogTitle className="font-display truncate pr-8">{file?.original_filename}</DialogTitle>
          <DialogDescription>{file && `${kb(file.size)} · uploaded by ${file.uploaded_by}`}</DialogDescription>
        </DialogHeader>
        <div className="flex min-h-[50vh] items-center justify-center rounded-md bg-slate-100">
          {!url || !file ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : isImage(file) ? (
            <img data-testid="file-preview-image" src={url} alt={file.original_filename} className="max-h-[72vh] max-w-full object-contain" />
          ) : (
            <iframe data-testid="file-preview-pdf" src={url} title={file.original_filename} className="h-[72vh] w-full rounded-md bg-white" />
          )}
        </div>
        <div className="flex justify-end">
          <Button data-testid="file-preview-download-button" variant="outline" disabled={!url} onClick={() => download(url, file.original_filename)}><Download className="h-4 w-4 mr-1" />Download</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Thumbs({ files, onOpen, testid }) {
  const [urls, setUrls] = useState({});
  const shown = files.filter(canPreview);
  const ids = shown.filter(isImage).map((f) => f.id).join(",");
  useEffect(() => {
    let alive = true;
    const made = [];
    Promise.all(shown.filter(isImage).map(async (f) => { const u = await fetchBlob(f).catch(() => null); if (u) made.push(u); return [f.id, u]; }))
      .then((pairs) => alive && setUrls(Object.fromEntries(pairs)));
    return () => { alive = false; made.forEach((u) => URL.revokeObjectURL(u)); };
  }, [ids]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!shown.length) return null;
  return (
    <div className="mb-2 grid grid-cols-3 sm:grid-cols-4 gap-2">
      {shown.map((f, i) => (
        <button key={f.id} type="button" data-testid={`${testid}-thumb-${i}`} onClick={() => onOpen(f)} title={f.original_filename}
          className="group relative aspect-[4/3] overflow-hidden rounded-md border border-slate-200 bg-slate-50 transition-shadow hover:shadow-md">
          {isImage(f) ? (urls[f.id] ? <img src={urls[f.id]} alt={f.original_filename} className="h-full w-full object-cover" /> : <Loader2 className="m-auto h-4 w-4 animate-spin text-slate-300" />) : (
            <div className="flex h-full flex-col items-center justify-center gap-1 text-rose-600"><FileText className="h-6 w-6" /><span className="font-mono text-[10px]">PDF</span></div>
          )}
          <span className="absolute inset-x-0 bottom-0 truncate bg-slate-900/70 px-1.5 py-0.5 text-[10px] text-white opacity-0 transition-opacity group-hover:opacity-100">{f.original_filename}</span>
        </button>
      ))}
    </div>
  );
}

export function FileBox({ entityType, entityId, category = "", title, canUpload = true, compact = false, testid = "files", accept }) {
  const { user, isAdmin } = useAuth();
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const ref = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/files", { params: { entity_id: entityId, ...(category ? { category } : {}) } });
    setFiles(data);
  }, [entityId, category]);
  useEffect(() => { if (entityId) load(); }, [load, entityId]);

  const upload = async (e) => {
    const list = [...e.target.files];
    e.target.value = "";
    if (!list.length) return;
    setBusy(true);
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("entity_type", entityType);
        fd.append("entity_id", entityId);
        fd.append("category", category);
        await api.post("/files", fd);
      }
      toast.success(`${list.length} file(s) uploaded`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setBusy(false);
  };

  const open = async (f) => {
    if (canPreview(f)) return setPreview(f);
    const u = await fetchBlob(f);
    download(u, f.original_filename);
    setTimeout(() => URL.revokeObjectURL(u), 2000);
  };

  const del = async (f) => {
    try { await api.delete(`/files/${f.id}`); toast.success("File removed"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const button = canUpload && (
    <>
      <input ref={ref} type="file" multiple accept={accept} className="hidden" onChange={upload} data-testid={`${testid}-upload-input`} />
      <button type="button" data-testid={`${testid}-upload-button`} disabled={busy} onClick={() => ref.current?.click()}
        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-zinc-700 transition-colors hover:bg-zinc-100 disabled:opacity-50">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}{busy ? "Uploading…" : "Upload"}
      </button>
    </>
  );

  const list = files.map((f, i) => (
    <div key={f.id} className="group flex items-center gap-2 text-xs">
      {canPreview(f) ? <Eye className="h-3 w-3 shrink-0 text-zinc-500" /> : <Paperclip className="h-3 w-3 shrink-0 text-slate-400" />}
      <button type="button" data-testid={`${testid}-file-${i}`} onClick={() => open(f)} title={canPreview(f) ? "Preview" : "Download"} className="truncate text-left text-slate-700 hover:text-blue-600 hover:underline">{f.original_filename}</button>
      {!compact && <span className="whitespace-nowrap text-slate-400">{kb(f.size)} · {f.uploaded_by}</span>}
      <span className="opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100"><WhatsAppButton testid={`${testid}-whatsapp-${i}`} iconOnly variant="ghost" text={`Hello,\n\nSharing "${f.original_filename}" with you.\n\nRegards,\nZero Design Studio`} attachment={{ id: f.id, name: f.original_filename }} projectId={entityType === "project" ? entityId : undefined} context={`File: ${f.original_filename}`} /></span>
      {(isAdmin || f.uploaded_by === user?.name) && (
        <button type="button" data-testid={`${testid}-delete-${i}`} onClick={() => del(f)} className="text-slate-300 opacity-0 transition-opacity hover:text-rose-600 group-hover:opacity-100"><Trash2 className="h-3 w-3" /></button>
      )}
    </div>
  ));

  const dialog = <PreviewDialog file={preview} onClose={() => setPreview(null)} />;
  if (compact) return <div className="space-y-1" data-testid={testid}>{list}{button}{dialog}</div>;
  return (
    <div data-testid={testid} className="rounded-lg border border-dashed border-slate-300 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{title}</span>{button}
      </div>
      <Thumbs files={files} onOpen={setPreview} testid={testid} />
      <div className="space-y-1.5">{files.length ? list : <div className="text-xs text-slate-400">No files uploaded yet.</div>}</div>
      {dialog}
    </div>
  );
}
